import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import InfoTip from "./InfoTip";
import { money, signed, sk, k, niceMax, ticksTo, COLORS } from "./metricsMath";

// Línea vertical punteada sobre el mes con el mouse (BarChart entrega banda x/width; ComposedChart entrega points).
export function VerticalCursor({ points, payload, x, y, width, height }) {
  if (!payload?.length) return null;
  const cx = points?.[0]?.x ?? (x != null && width != null ? x + width / 2 : null);
  const y1 = points?.[0]?.y ?? y, y2 = points?.[1]?.y ?? (y != null && height != null ? y + height : null);
  if (cx == null || y1 == null || y2 == null) return null;
  return <g data-testid="flow-cursor"><line x1={cx} x2={cx} y1={y1} y2={y2} stroke="#002FA7" strokeWidth={1.5} strokeDasharray="4 4" /></g>;
}

export function TipBox({ title, lines, testid }) {
  return (
    <div className="bg-white border border-zinc-200 shadow-lg rounded-md p-2.5 text-xs space-y-1 min-w-[210px]" data-testid={testid}>
      <div className="text-zinc-500 capitalize">{title}</div>
      {lines.map(([color, label, value, cls]) => (
        <div key={label} className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm shrink-0" style={{ background: color || "transparent" }} /><span className="text-zinc-600">{label}</span><b className={`ml-auto pl-4 numeric ${cls || "text-zinc-900"}`}>{value}</b></div>
      ))}
    </div>
  );
}

// Mes arriba, neto del mes debajo (verde si entró más de lo que salió).
const MonthTick = ({ x, y, payload, rows }) => {
  const r = rows[payload.index];
  return (
    <g transform={`translate(${x},${y})`}>
      <text y={12} textAnchor="middle" fontSize={11} fill="#71717a">{payload.value}</text>
      {r && <text y={28} textAnchor="middle" fontSize={11} fontWeight={700} fill={r.neto > 0 ? COLORS.in : r.neto < 0 ? COLORS.out : "#71717a"} data-testid={`net-label-${r.month}`}>{sk(r.neto)}</text>}
    </g>
  );
};

const FlowTip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const r = payload[0].payload;
  return <TipBox testid="flow-tooltip" title={r.mesLargo} lines={[[COLORS.in, "Entró", money(r.entro)], [COLORS.out, "Salió", money(r.salio)], [null, "Neto", signed(r.neto), r.neto > 0 ? "text-[#00873D]" : r.neto < 0 ? "text-[#E0000F]" : "text-zinc-500"], [COLORS.gas, "Gastos registrados", money(r.gastos)]]} />;
};

export default function FlowChart({ rows }) {
  const top = niceMax(Math.max(0, ...rows.map((r) => Math.max(r.entro || 0, r.salio || 0))));
  return (
    <section className="border border-zinc-200 bg-white rounded-lg p-5 flex flex-col gap-2 min-w-0" data-testid="flow-section">
      <h3 className="font-heading font-bold text-base m-0">Entradas vs salidas por mes</h3>
      <p className="text-xs text-zinc-500 m-0 leading-relaxed">Barras: dinero que entró y salió de los bancos. Debajo de cada mes, el neto (verde si entró más de lo que salió).</p>
      <div className="flex flex-wrap gap-4 text-xs text-zinc-500" data-testid="flow-legend">
        <span className="inline-flex items-center gap-1.5"><i className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: COLORS.in }} />Entró <InfoTip k="inflow" /></span>
        <span className="inline-flex items-center gap-1.5"><i className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: COLORS.out }} />Salió <InfoTip k="outflow" /></span>
        <span className="inline-flex items-center gap-1.5"><b className="text-[#00873D]">+k</b> Neto del mes <InfoTip k="net" /></span>
      </div>
      <div className="h-[280px]" data-testid="flow-chart">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows} margin={{ top: 10, right: 8, left: 0, bottom: 20 }} barGap={3} barCategoryGap="28%">
            <CartesianGrid strokeDasharray="3 3" stroke="#f0eeea" vertical={false} />
            <XAxis dataKey="mes" interval={0} tick={<MonthTick rows={rows} />} axisLine={{ stroke: "#d4d4d8" }} tickLine={false} height={40} />
            <YAxis tickFormatter={k} tick={{ fontSize: 11, fill: "#71717a" }} axisLine={false} tickLine={false} width={44} domain={[0, top]} ticks={ticksTo(top)} />
            <Tooltip content={<FlowTip />} cursor={<VerticalCursor />} />
            <Bar dataKey="entro" name="Entró" fill={COLORS.in} radius={[3, 3, 0, 0]} maxBarSize={32} />
            <Bar dataKey="salio" name="Salió" fill={COLORS.out} radius={[3, 3, 0, 0]} maxBarSize={32} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}
